import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TOC } from "../../data/toc";
import { Reveal } from "../../lib/motion";
import { BuyButton, SectionLabel } from "./ui";

const EASE = [0.22, 1, 0.36, 1];

export function BookChapters() {
  const [[page, dir], setPage] = useState([0, 0]);
  const total = TOC.length;
  const entry = TOC[page];

  const paginate = (d) =>
    setPage(([p]) => [(p + d + total) % total, d]);

  const variants = {
    enter: (d) => ({ rotateY: d > 0 ? 32 : -32, opacity: 0, x: d > 0 ? 70 : -70 }),
    center: { rotateY: 0, opacity: 1, x: 0 },
    exit: (d) => ({ rotateY: d > 0 ? -32 : 32, opacity: 0, x: d > 0 ? -70 : 70 }),
  };

  return (
    <section id="book" className="relative py-24 sm:py-36 bg-primary" data-testid="book-section" data-section="book" data-section-label="Indice del libro">
      <div className="max-w-6xl mx-auto px-5 sm:px-10">
        <Reveal><SectionLabel num="07">Table of Contents</SectionLabel></Reveal>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <Reveal>
            <h2 className="font-editorial italic text-3xl sm:text-5xl text-cream leading-tight max-w-2xl">
              What the reader finds within the Table of Contents.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-body font-light text-muted text-sm max-w-xs">
              Turn the pages of the real index — chapter by chapter, with page numbers.
            </p>
          </Reveal>
        </div>

        <div className="mt-14" data-testid="book-index" style={{ perspective: "1800px" }}>
          <div className="relative bg-secondary gold-border rounded-2xl overflow-hidden">
            <div className="relative">
              <AnimatePresence mode="wait" custom={dir}>
                <motion.div
                  key={page}
                  custom={dir}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.5, ease: EASE }}
                  style={{ transformOrigin: dir > 0 ? "left center" : "right center" }}
                  className="px-6 sm:px-14 py-10 sm:py-12"
                  data-testid={`book-page-${page}`}
                >
                  <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
                    {/* Left: chapter marker */}
                    <div className="lg:col-span-4">
                      <div className="lg:sticky lg:top-24">
                        <span className="font-display text-6xl sm:text-8xl gold-gradient-text leading-none block">
                          {entry.big}
                        </span>
                        <span className="font-label text-[0.65rem] text-muted tracking-[0.24em] block mt-4">
                          {entry.tag.toUpperCase()} · P. {entry.startPage}
                        </span>
                        <h3 className="font-editorial text-2xl sm:text-3xl text-cream leading-tight mt-4 max-w-xs">
                          {entry.title}
                        </h3>
                        <div className="divider-ornament my-6 max-w-[200px] !justify-start">
                          <span className="line" />
                        </div>
                      </div>
                    </div>

                    {/* Right: entries with dotted leaders */}
                    <div className="lg:col-span-8">
                      <ul
                        className="max-h-[46vh] lg:max-h-[420px] overflow-y-auto pr-2 sm:pr-4 space-y-1.5 toc-scroll"
                        style={{ overscrollBehavior: "auto", WebkitOverflowScrolling: "touch" }}
                        data-lenis-prevent
                        onWheel={(e) => {
                          const el = e.currentTarget;
                          const hasOverflow = el.scrollHeight > el.clientHeight + 1;
                          if (!hasOverflow) return;
                          const atTop = el.scrollTop <= 0;
                          const atBottom = el.scrollTop >= el.scrollHeight - el.clientHeight - 1;
                          const goingDown = e.deltaY > 0;
                          const goingUp = e.deltaY < 0;
                          if ((atBottom && goingDown) || (atTop && goingUp)) {
                            window.scrollBy({ top: e.deltaY, behavior: "auto" });
                          }
                        }}
                        data-testid={`toc-list-${page}`}
                      >
                        {entry.items.map((it, i) => (
                          <li
                            key={i}
                            className={`flex items-baseline gap-2 ${it.indent ? "pl-5 sm:pl-8" : ""}`}
                            data-testid={`toc-item-${page}-${i}`}
                          >
                            <span
                              className={`font-body text-sm sm:text-[0.95rem] leading-snug min-w-0 ${
                                it.ref
                                  ? "italic text-muted"
                                  : it.indent
                                  ? "text-secondary"
                                  : "text-cream"
                              }`}
                            >
                              {it.t}
                            </span>
                            <span className="flex-1 min-w-[18px] border-b border-dotted border-[var(--gold-line)] translate-y-[-2px]" />
                            <span className="font-label text-xs text-gold shrink-0">{it.p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between px-5 sm:px-8 py-5 border-t border-[var(--gold-line)] bg-[rgba(10,9,8,0.45)]">
              <button
                onClick={() => paginate(-1)}
                data-testid="book-prev"
                aria-label="Previous section"
                className="btn-ghost-gold rounded-full p-3 shrink-0"
              >
                <ChevronLeft size={20} />
              </button>

              <div className="flex items-center gap-1.5 flex-wrap justify-center px-3">
                {TOC.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage([i, i > page ? 1 : -1])}
                    data-testid={`book-dot-${i}`}
                    aria-label={`Go to section ${i + 1}`}
                    className="py-3 px-1 flex items-center group"
                  >
                    <span
                      className={`block h-1.5 rounded-full transition-all duration-300 ${
                        i === page ? "w-7 bg-[var(--gold)]" : "w-1.5 bg-[var(--gold-line)] group-hover:bg-[var(--gold-dark)]"
                      }`}
                    />
                  </button>
                ))}
              </div>

              <button
                onClick={() => paginate(1)}
                data-testid="book-next"
                aria-label="Next section"
                className="btn-ghost-gold rounded-full p-3 shrink-0"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16 flex flex-wrap items-center gap-6">
            <BuyButton location="book" size="lg" testId="buy-btn-book" />
            <span className="font-editorial italic text-secondary text-lg">Premium edition · €40 · 225 pages</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
